// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useCallback, useEffect, useState } from "react";
import scenarioService from "../services/scenarioService";
import useCaseService from "../services/useCaseService";

/**
 * Scenario-level config for the Map Editor.
 *
 * Loads:
 *  - the scenario record (name, code, description, location, tenant…)
 *  - the catalog of available use cases
 *  - the use cases currently assigned to this scenario (with priority,
 *    price_multiplier, is_active)
 *
 * Exposes `persistAll({ scenarioPatch, assignments })` which diffs the
 * desired use-case assignments against the loaded ones and issues
 * POST/DELETE, plus a PUT for any scenario-level field changes.
 *
 * The shape mirrors `useScenarioSpaces` so the editor can drive both from a
 * single Speichern button.
 */
const normalizeAssignment = (raw) => ({
  id: raw.id ?? raw.assignment_id ?? null,
  use_case_id: raw.use_case_id ?? raw.useCaseId ?? raw.use_case?.id,
  name: raw.name ?? raw.use_case?.name ?? "",
  is_active: raw.is_active !== false,
  priority: raw.priority ?? 0,
  price_multiplier: raw.price_multiplier ?? 1,
});

const useScenarioConfig = (scenarioId) => {
  const [scenario, setScenario] = useState(null);
  const [useCases, setUseCases] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(() => Boolean(scenarioId));
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!scenarioId) {
      setScenario(null);
      setAssignments([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [scenarioResp, ucResp, assignedResp] = await Promise.all([
        scenarioService.getScenarioById(scenarioId),
        useCaseService.listUseCases(),
        useCaseService.getScenarioUseCases(scenarioId),
      ]);
      setScenario(scenarioResp?.data ?? scenarioResp ?? null);
      const ucItems = Array.isArray(ucResp)
        ? ucResp
        : (ucResp?.items ?? ucResp?.data ?? ucResp?.results ?? []);
      setUseCases(ucItems);
      const assignedItems = Array.isArray(assignedResp)
        ? assignedResp
        : (assignedResp?.items ??
          assignedResp?.data ??
          assignedResp?.results ??
          []);
      setAssignments(assignedItems.map(normalizeAssignment));
    } catch (err) {
      console.error("Error loading scenario config:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [scenarioId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  /**
   * Persist a scenario-level change. `desiredAssignments` is the list the
   * admin wants on this scenario after save — we diff it against the
   * currently-loaded list, POST new entries, DELETE removed ones.
   *
   * Pricing/priority edits to existing rows are sent as remove + reassign
   * because the backend has no PUT for scenario-level use cases (it does
   * for space-level). Cheap enough at this scope (handful of use cases).
   *
   * `scenarioPatch` is an object of scenario columns to PUT alongside.
   * Pass an empty object to skip the scenario PUT.
   */
  const persistAll = useCallback(
    async ({ scenarioPatch = null, desiredAssignments = null } = {}) => {
      if (!scenarioId) {
        throw new Error("Cannot persist scenario config without a scenarioId");
      }
      const errors = [];

      if (scenarioPatch && Object.keys(scenarioPatch).length > 0) {
        try {
          await scenarioService.updateScenario(scenarioId, scenarioPatch);
        } catch (e) {
          errors.push({ op: "scenario_update", error: e });
        }
      }

      if (Array.isArray(desiredAssignments)) {
        const desiredById = new Map(
          desiredAssignments
            .filter((a) => a?.use_case_id != null)
            .map((a) => [a.use_case_id, a]),
        );
        const loadedById = new Map(assignments.map((a) => [a.use_case_id, a]));

        // Drop removed.
        for (const [ucId] of loadedById) {
          if (!desiredById.has(ucId)) {
            try {
              await useCaseService.removeScenarioUseCase(scenarioId, ucId);
            } catch (e) {
              errors.push({ op: "uc_remove", use_case_id: ucId, error: e });
            }
          }
        }

        // Add/replace. The endpoint has POST only at scenario scope, so
        // edits to priority/multiplier go through delete+post.
        for (const [ucId, desired] of desiredById) {
          const prev = loadedById.get(ucId);
          const changed =
            !prev ||
            prev.priority !== desired.priority ||
            prev.price_multiplier !== desired.price_multiplier ||
            prev.is_active !== desired.is_active;
          if (!changed) continue;
          if (prev) {
            try {
              await useCaseService.removeScenarioUseCase(scenarioId, ucId);
            } catch (e) {
              errors.push({
                op: "uc_replace_remove",
                use_case_id: ucId,
                error: e,
              });
              continue;
            }
          }
          try {
            await useCaseService.assignScenarioUseCase(scenarioId, {
              use_case_id: ucId,
              is_active: desired.is_active !== false,
              priority: Number(desired.priority) || 0,
              price_multiplier: Number(desired.price_multiplier) || 1,
            });
          } catch (e) {
            errors.push({ op: "uc_assign", use_case_id: ucId, error: e });
          }
        }
      }

      await refetch();
      return { errors };
    },
    [scenarioId, assignments, refetch],
  );

  return {
    scenario,
    useCases,
    assignments,
    loading,
    error,
    refetch,
    persistAll,
  };
};

export default useScenarioConfig;
